import React from "react";
import {
  Create,
  DataTable,
  List,
  Pagination,
  SimpleForm,
  TextField,
  FileField,
  FileInput,
  TopToolbar,
  ColumnsButton,
  CreateButton,
  useRecordContext,
  Show,
  SimpleShowLayout,
} from "react-admin";
import { CustomDatetimeField } from "../lib/datetime";
import { InboundTaskStatsBox } from "./tasks";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { DetailsButton } from "../lib/detailsButton";
import { Accordion } from "@mui/material";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

export const FileStatusField = (_props?: { source?: string }) => {
  const rec = useRecordContext();

  let color: string;
  if (rec.status === "success") {
    color = "#00A87B";
  } else if (rec.status === "pending") {
    color = "#BF5C00";
  } else if (rec.status === "running") {
    color = "#BF5C00";
  } else {
    color = "#A20021";
  }
  return rec.status === "error" ? (
    <Tooltip
      title={
        <p
          style={{
            fontSize: "1.8em",
            lineHeight: 1,
            paddingLeft: "15px",
            paddingRight: "15px",
          }}
        >
          {rec.error}
        </p>
      }
    >
      <Typography
        variant="body2"
        component="span"
        noWrap={true}
        sx={{ color: color }}
      >
        {rec.status}
      </Typography>
    </Tooltip>
  ) : (
    <Typography
      variant="body2"
      component="span"
      noWrap={true}
      sx={{ color: color }}
    >
      {rec.status}
    </Typography>
  );
};

export const DocumentList = () => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    exporter={false}
    actions={
      <TopToolbar>
        <CreateButton />
        <ColumnsButton />
      </TopToolbar>
    }
    sort={{ field: "timestamp", order: "DESC" }}
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="name" label="Name" disableSort>
        <TextField source="name" label="Name" />
      </DataTable.Col>
      <DataTable.Col source="timestamp" label="Time" disableSort>
        <CustomDatetimeField source="timestamp" label="Time" />
      </DataTable.Col>
      <DataTable.Col source="format" label="Format" disableSort>
        <TextField source="format" label="Format" />
      </DataTable.Col>
      <DataTable.Col source="sizeKB" label="Size (KB)" disableSort>
        <TextField source="sizeKB" label="Size (KB)" />
      </DataTable.Col>
      <DataTable.Col source="status" label="Status" disableSort>
        <FileStatusField source="status" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const DocumentMessage = () => {
  return (
    <div style={{ marginBottom: 30 }}>
      <h2>Data Definitions</h2>
      <Accordion style={{ width: "100%" }}>
        <AccordionSummary style={{ backgroundColor: "#f5f5f5" }}>
          <b>Full Format (Blends, Lots, Barrels)</b>
        </AccordionSummary>
        <AccordionDetails>
          <p>
            Blends, Lots, and Barrel relationships and red fields will be
            updated. Unincluded entities will be archived.
          </p>
          <p>
            <b>Fields: </b>Blend Name, Blend Creation Date, Vintage Year,
            Varietal, Wine Color, Lot Name, Lot Creation Date,{" "}
            <span style={{ color: "red" }}>
              Addition Target, Minimum Addition, Maximum Addition, Addition
              Multiplier,
            </span>{" "}
            Intended Use, Barcode, Alias,{" "}
            <span style={{ color: "red" }}>Cooper, Toast, Oak, Forest, </span>
            Label,{" "}
            <span style={{ color: "red" }}>
              Capacity, Capacity Unit, Barrel Year,{" "}
            </span>
            Seasoning, Drying Time, Toasted Head, Grain,{" "}
            <span style={{ color: "red" }}>Type</span>
          </p>
          <a
            href="data:text/csv;charset=utf-8,Blend Name%2CBlend Creation Date%2CVintage Year%2CVarietal%2CWine Color%2CLot Name%2CLot Creation Date%2CAddition Target%2CMinimum Addition%2CMaximum Addition%2CAddition Multiplier%2CIntended Use%2CBarcode%2CAlias%2CCooper%2CToast%2COak%2CForest%2CLabel%2CCapacity%2CCapacity Unit%2CBarrel Year%2CSeasoning%2CDrying Time%2CToasted Head%2CGrain%2CType"
            download="data.csv"
          >
            Download Template
          </a>
        </AccordionDetails>
      </Accordion>
      <Accordion style={{ width: "100%" }}>
        <AccordionSummary style={{ backgroundColor: "#f5f5f5" }}>
          <b>Barrels Only</b>
        </AccordionSummary>
        <AccordionDetails>
          <p>
            Barrel metadata fields in red will be updated, but not their
            relationships to Lots. Unincluded barrels will be ignored.
          </p>
          <p>
            <b>Fields: </b>Barcode, Alias,{" "}
            <span style={{ color: "red" }}>Cooper, Toast, Oak, Forest,</span>{" "}
            Label,
            <span style={{ color: "red" }}>
              {" "}
              Capacity, Capacity Unit, Barrel Year,
            </span>{" "}
            Seasoning, Drying Time, Toasted Head, Grain,{" "}
            <span style={{ color: "red" }}>Type</span>
          </p>
          <a
            href="data:text/csv;charset=utf-8,Barcode%2CAlias%2CCooper%2CToast%2COak%2CForest%2CLabel%2CCapacity%2CCapacity Unit%2CBarrel Year%2CSeasoning%2CDrying Time%2CToasted Head%2CGrain%2CType"
            download="data.csv"
          >
            Download Template
          </a>
        </AccordionDetails>
      </Accordion>
      <Accordion style={{ width: "100%" }}>
        <AccordionSummary style={{ backgroundColor: "#f5f5f5" }}>
          <b>Lots Only</b>
        </AccordionSummary>
        <AccordionDetails>
          <p>
            Lot metadata fields in red will be updated, but not their
            relationships to Barrels. Blends must already exist. You can leave
            the blend name blank and it will use the "Unknown" blend. Unincluded
            lots will be ignored.
          </p>
          <p>
            <b>Fields: </b>Blend Name, Lot Name, Lot Creation Date,{" "}
            <span style={{ color: "red" }}>
              Addition Target, Minimum Addition, Maximum Addition, Addition
              Multiplier,
            </span>{" "}
            Intended Use
          </p>
          <a
            href="data:text/csv;charset=utf-8,Blend Name%2CLot Name%2CLot Creation Date%2CAddition Target%2CMinimum Addition%2CMaximum Addition%2CAddition Multiplier%2CIntended Use"
            download="data.csv"
          >
            Download Template
          </a>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export function DocumentCreate() {
  return (
    <Create redirect="list">
      <SimpleForm>
        <DocumentMessage />
        <h2>Upload .csv file (Max. 10MB)</h2>
        <FileInput source="attachment" accept={".csv" as any} maxSize={100000000}>
          <FileField source="src" title="title" />
        </FileInput>
      </SimpleForm>
    </Create>
  );
}

const StatsBox = () => {
  const record = useRecordContext();

  if (record.status === "success") {
    return (
      <>
        <InboundTaskStatsBox />
      </>
    );
  }
};

export const DocumentShow = () => {
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="id" />
        <TextField source="name" label="Name" />
        <CustomDatetimeField source="timestamp" />
        <TextField source="format" label="Format" />
        <TextField source="sizeKB" label="Size (KB)" />
        <FileStatusField source="status" />
        <TextField source="error" label="Error" style={{ whiteSpace: "pre" }} />
        <StatsBox />
      </SimpleShowLayout>
    </Show>
  );
};
